// src/utils/ticketStatus.js
// -----------------------------------------------------------------------------
// Status lists and their order are configured per project (Requirement #16),
// so pages must not assume fixed status IDs (e.g. 1 = Open, 4 = Closed).
// These helpers classify a ticket using its project's status settings:
//   - closed:      the status has Is_Closed_Status_Flag
//   - new:         the first status in the project's sequence (not closed)
//   - in progress: any other open status
// -----------------------------------------------------------------------------

const toNumber = (v) => Number(v ?? 0);

export function buildStatusHelpers(statuses = []) {
  const byId = new Map(statuses.map((s) => [toNumber(s.statusID ?? s.statusId), s]));

  // Lowest sequence order per project = the "new" status
  const firstByProject = new Map();
  statuses.forEach((s) => {
    const projectId = toNumber(s.projectID ?? s.projectId);
    const current = firstByProject.get(projectId);
    if (!current || toNumber(s.sequenceOrder) < toNumber(current.sequenceOrder)) firstByProject.set(projectId, s);
  });

  const statusOf = (ticket) => byId.get(toNumber(ticket?.statusID ?? ticket?.statusId));

  const isClosed = (ticket) => {
    const status = statusOf(ticket);
    if (status) return !!status.isClosedStatusFlag;
    // Status not loaded: fall back to the resolved date / name
    const name = String(ticket?.statusName || '').toUpperCase();
    return !!ticket?.resolvedDate || name === 'RESOLVED' || name === 'CLOSED';
  };

  const isNew = (ticket) => {
    const status = statusOf(ticket);
    if (!status || status.isClosedStatusFlag) return false;
    const first = firstByProject.get(toNumber(status.projectID ?? status.projectId));
    return !!first && toNumber(first.statusID ?? first.statusId) === toNumber(status.statusID ?? status.statusId);
  };

  const isInProgress = (ticket) => !isClosed(ticket) && !isNew(ticket);

  return { statusOf, isClosed, isNew, isInProgress };
}

export default buildStatusHelpers;
